import { ActivityIndicator, DropdownMenu } from '@kontentino/ui';
import WorkflowStatusBadge from 'app/components/workflowStatus/WorkflowStatusBadge';
import WorkflowStatusIcon from 'app/components/workflowStatus/WorkflowStatusIcon';
import { FC } from 'react';
import { useUser } from 'modules/user/userSelector';
import { useTranslation } from 'react-i18next';
import { PostDetail } from 'types/PostDetail';
import clsx from 'clsx';
import { confirmStatusChangeAwayFromPublished } from 'utils/statuses';
import { filterManuallySettableStatuses } from 'app/modules/posts/utils/manuallySettableStatuses';

type Props = {
  post: PostDetail;
  onSelectStatus: (status: number) => void;
  isDisabled?: boolean;
  isLoading?: boolean;
  loadingStatus?: number;
};

const WorkflowStatusBadgeDropdown: FC<Props> = ({
  post,
  onSelectStatus,
  isDisabled,
  isLoading,
  loadingStatus,
}) => {
  const { availableStatuses, permissionsKeys, role } = useUser();

  const { t } = useTranslation();

  const statusesOptions = filterManuallySettableStatuses(
    availableStatuses([post.page.type]),
    permissionsKeys,
    role,
    'postDetail',
  ).map((status) => ({
    value: status,
    label: t(`workflowStatus.label.${status}`),
  }));

  const handleSelectStatus = async (status: number) => {
    const confirmed = await confirmStatusChangeAwayFromPublished(
      post.status,
      status,
      t,
    );

    if (!confirmed) {
      return;
    }

    onSelectStatus(status);
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        className={clsx('tw-group/status-badge', {
          'tw-cursor-pointer': !isDisabled && !isLoading,
          'tw-cursor-default': isDisabled || isLoading,
        })}
        disabled={isDisabled || isLoading}
        data-name="workflow-status-badge-dropdown"
      >
        <div>
          <WorkflowStatusBadge
            status={post.status}
            assignedUsers={post.assigned_users}
            showChevron={!isDisabled}
            isLoading={isLoading}
          />
        </div>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content side="bottom" className="tw-z-10">
        {statusesOptions.map((status) => (
          <div
            key={`status-${status.value}`}
            data-name={`workflow-status-badge-dropdown-option_${status.value}`}
            data-cy={`workflow-status-badge-dropdown-option_${status.value}`}
            className="tw-select-none"
          >
            <DropdownMenu.Item
              selected={post.status === status.value}
              disabled={isLoading}
              onSelect={() => handleSelectStatus(status.value)}
            >
              {isLoading && loadingStatus === status.value ? (
                <ActivityIndicator className="!tw-border-t-primary-100" />
              ) : (
                <WorkflowStatusIcon status={status.value} />
              )}
              <span className="tw-max-w-[200px] tw-truncate tw-font-regular">
                {status.label}
              </span>
            </DropdownMenu.Item>
          </div>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

export default WorkflowStatusBadgeDropdown;
